'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import Image from 'next/image';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Service {
  icon: string;
  title: string;
  description: string;
}

interface WhyItem {
  icon: string;
  title: string;
  description: string;
}

interface GalleryItem {
  src: string;
  alt: string;
  label: string;
}

// ─── Data ────────────────────────────────────────────────────────────────────
const services: Service[] = [
{
  icon: 'fas fa-tools',
  title: 'Réparation PC & Imprimantes',
  description: 'Diagnostic précis et réparation rapide de vos équipements informatiques et imprimantes.'
},
{
  icon: 'fas fa-laptop',
  title: 'Vente de Matériels Informatiques',
  description: 'Du matériel performant et fiable adapté à vos besoins professionnels et personnels.'
},
{
  icon: 'fas fa-stethoscope',
  title: 'Maintenance & Diagnostic',
  description: 'Maintenance préventive et diagnostic complet pour une performance optimale de vos équipements.'
},
{
  icon: 'fas fa-network-wired',
  title: 'Installation & Entretien Réseau',
  description: 'Installation, configuration et entretien de vos réseaux informatiques professionnels.'
},
{
  icon: 'fas fa-headset',
  title: 'Assistance & Conseils',
  description: 'Accompagnement et conseils personnalisés pour tous vos projets informatiques.'
},
{
  icon: 'fas fa-fire-extinguisher',
  title: 'Détection Incendie',
  description: 'Installation et maintenance de systèmes de détection incendie pour la sécurité de vos locaux.'
},
{
  icon: 'fas fa-video',
  title: 'Vidéo Surveillance',
  description: 'Installation de systèmes de vidéosurveillance performants pour protéger vos biens et vos équipes.'
},
{
  icon: 'fas fa-phone-alt',
  title: 'Téléphonie',
  description: 'Solutions de téléphonie IP et traditionnelle pour optimiser vos communications professionnelles.'
}];


const whyItems: WhyItem[] = [
{
  icon: 'fas fa-star',
  title: 'Qualité',
  description: 'Matériel & service de haute qualité pour des résultats durables.'
},
{
  icon: 'fas fa-shield-alt',
  title: 'Fiabilité',
  description: 'Des solutions fiables et durables sur lesquelles vous pouvez compter.'
},
{
  icon: 'fas fa-bolt',
  title: 'Rapidité',
  description: 'Intervention rapide et efficace pour minimiser vos temps d\'arrêt.'
},
{
  icon: 'fas fa-thumbs-up',
  title: 'Satisfaction',
  description: 'Votre satisfaction est notre priorité absolue à chaque intervention.'
}];


const galleryItems: GalleryItem[] = [
{
  src: "/assets/images/zzzz-1785424840317.jpg",
  alt: 'Réparation PC et composants informatiques',
  label: 'Réparation PC & Matériel'
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1924b658e-1785421346555.png",
  alt: 'Vente de matériel informatique neuf',
  label: 'Vente Matériel Informatique'
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1c146337b-1784488345946.png",
  alt: 'Installation réseau informatique professionnel',
  label: 'Installation Réseau'
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1c7da3aa2-1785421345973.png",
  alt: 'Maintenance et diagnostic informatique',
  label: 'Maintenance & Diagnostic'
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1a0434f50-1784727893966.png",
  alt: 'Infrastructure réseau et câblage',
  label: 'Infrastructure Réseau'
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1c38f0ba6-1773131683863.png",
  alt: 'Assistance et support informatique',
  label: 'Assistance Informatique'
},
{
  src: "/assets/images/fire_detection_gallery.png",
  alt: 'Système de détection incendie professionnel avec détecteurs de fumée et centrale alarme',
  label: 'Détection Incendie'
},
{
  src: "/assets/images/video_surveillance_gallery.png",
  alt: 'Installation vidéo surveillance avec caméras CCTV et poste de monitoring',
  label: 'Vidéo Surveillance'
},
{
  src: "/assets/images/telephonie_grandstream_gallery.png",
  alt: 'Système de téléphonie IP Grandstream avec téléphones VoIP professionnels',
  label: 'Téléphonie Grandstream'
}];


// ─── Particle Canvas Hook ─────────────────────────────────────────────────────
function useParticleCanvas(canvasRef: React.RefObject<HTMLCanvasElement | null>) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    interface P {
      x: number;y: number;size: number;
      speedX: number;speedY: number;opacity: number;
    }

    const particles: P[] = Array.from({ length: 100 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 2 + 0.5,
      speedX: Math.random() * 0.5 - 0.25,
      speedY: Math.random() * 0.5 - 0.25,
      opacity: Math.random() * 0.5 + 0.1
    }));

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.speedX;
        p.y += p.speedY;
        if (p.x > canvas.width) p.x = 0;
        if (p.x < 0) p.x = canvas.width;
        if (p.y > canvas.height) p.y = 0;
        if (p.y < 0) p.y = canvas.height;
        ctx.fillStyle = `rgba(0, 112, 112, ${p.opacity})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      });
      particles.forEach((a, i) => {
        particles.slice(i + 1).forEach((b) => {
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.strokeStyle = `rgba(0, 112, 112, ${0.1 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        });
      });
      animId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, [canvasRef]);
}

// ─── Fade-in Observer Hook ────────────────────────────────────────────────────
function useFadeInObserver() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('vm-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    document.querySelectorAll('.vm-fade').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function VMNetworksPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [lightboxAlt, setLightboxAlt] = useState('');
  const [formState, setFormState] = useState({
    nom: '', prenom: '', email: '', telephone: '', service: '', message: ''
  });
  const [formSent, setFormSent] = useState(false);
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  useParticleCanvas(canvasRef);
  useFadeInObserver();

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleFormChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormState((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  const handleFormSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formState)
      });
      const data = await res.json();
      if (!res.ok) {
        setFormError(data.error || 'Une erreur est survenue. Veuillez réessayer.');
      } else {
        setFormSent(true);
        setFormState({ nom: '', prenom: '', email: '', telephone: '', service: '', message: '' });
        setTimeout(() => setFormSent(false), 4000);
      }
    } catch {
      setFormError('Impossible d\'envoyer le message. Vérifiez votre connexion.');
    } finally {
      setFormLoading(false);
    }
  }, [formState]);

  const navLinks = [
  { href: '#home', label: 'Accueil' },
  { href: '#about', label: 'À Propos' },
  { href: '#services', label: 'Services' },
  { href: '#gallery', label: 'Galerie' },
  { href: '#why', label: 'Pourquoi Nous' },
  { href: '#contact', label: 'Contact' }];


  return (
    <>
      <style>{`
        :root {
          --primary: #007070;
          --primary-dark: #005555;
          --primary-light: #00A0A0;
          --dark: #0A0A0A;
          --dark-light: #1A1A1A;
          --gray: #2A2A2A;
          --light: #F5F5F5;
          --white: #FFFFFF;
          --accent: #00D4D4;
          --navy: #1a2332;
          --gradient: linear-gradient(135deg, #007070 0%, #00A0A0 50%, #00D4D4 100%);
        }
        html { scroll-behavior: smooth; }
        body { font-family: 'Inter', sans-serif; background: var(--dark); color: var(--light); overflow-x: hidden; }
        #vm-canvas { position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: -1; opacity: 0.3; }

        /* Nav */
        .vm-nav {
          position: fixed; top: 0; width: 100%; padding: 1rem 5%;
          display: flex; justify-content: space-between; align-items: center;
          z-index: 1000; backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(0,112,112,0.15);
          transition: all 0.3s;
        }
        .vm-nav.scrolled { background: rgba(10,10,10,0.97); box-shadow: 0 5px 20px rgba(0,0,0,0.4); }
        .vm-nav:not(.scrolled) { background: rgba(10,10,10,0.85); }

        .vm-logo-text {
          font-family: 'Orbitron', sans-serif; font-size: 1.6rem; font-weight: 900;
          background: var(--gradient); -webkit-background-clip: text;
          -webkit-text-fill-color: transparent; background-clip: text; letter-spacing: 2px;
          line-height: 1.1;
        }
        .vm-logo-sub {
          font-family: 'Inter', sans-serif; font-size: 0.7rem; font-weight: 400;
          -webkit-text-fill-color: rgba(245,245,245,0.7); letter-spacing: 1px;
          display: block; margin-top: 2px;
        }

        .vm-nav-links { display: flex; gap: 2rem; list-style: none; align-items: center; }
        .vm-nav-links a {
          color: var(--light); text-decoration: none; font-weight: 500;
          font-size: 0.85rem; text-transform: uppercase; letter-spacing: 1px;
          transition: all 0.3s; position: relative;
        }
        .vm-nav-links a::after {
          content: ''; position: absolute; bottom: -5px; left: 0;
          width: 0; height: 2px; background: var(--primary); transition: width 0.3s;
        }
        .vm-nav-links a:hover::after { width: 100%; }
        .vm-nav-links a:hover { color: var(--accent); }

        .vm-mobile-btn { display: none; font-size: 1.5rem; cursor: pointer; color: var(--primary); background: none; border: none; }

        /* Hero */
        .vm-hero {
          min-height: 100vh; display: flex; align-items: center; justify-content: center;
          text-align: center; padding: 0 5%; position: relative; overflow: hidden;
        }
        .vm-hero::before {
          content: ''; position: absolute; top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          width: 600px; height: 600px;
          background: radial-gradient(circle, rgba(0,112,112,0.18) 0%, transparent 70%);
          border-radius: 50%; animation: vm-pulse 4s ease-in-out infinite;
        }
        @keyframes vm-pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-50%, -50%) scale(1.2); }
        }
        .vm-hero-content { position: relative; z-index: 1; }
        .vm-badge {
          display: inline-block; font-family: 'Orbitron', sans-serif;
          font-size: 0.75rem; color: var(--accent); letter-spacing: 3px;
          margin-bottom: 1.2rem; text-transform: uppercase;
          border: 1px solid rgba(0,212,212,0.3); padding: 0.4rem 1.2rem;
          border-radius: 25px; background: rgba(0,112,112,0.1);
        }
        .vm-hero h1 {
          font-family: 'Orbitron', sans-serif;
          font-size: clamp(2rem, 5.5vw, 4.5rem);
          font-weight: 900; margin-bottom: 1rem; line-height: 1.1;
        }
        .vm-gradient-text {
          background: var(--gradient); -webkit-background-clip: text;
          -webkit-text-fill-color: transparent; background-clip: text;
        }
        .vm-hero p {
          font-size: 1.15rem; margin-bottom: 2.5rem; opacity: 0.85;
          max-width: 580px; margin-left: auto; margin-right: auto; line-height: 1.7;
        }
        .vm-cta-buttons { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }

        /* Buttons */
        .vm-btn {
          padding: 1rem 2.5rem; border-radius: 50px; text-decoration: none;
          font-weight: 600; font-size: 0.9rem; transition: all 0.3s;
          display: inline-block; text-transform: uppercase; letter-spacing: 1px;
          cursor: pointer; border: none;
        }
        .vm-btn-primary {
          background: var(--gradient); color: var(--white);
          box-shadow: 0 10px 30px rgba(0,112,112,0.35);
        }
        .vm-btn-primary:hover { transform: translateY(-3px); box-shadow: 0 15px 40px rgba(0,112,112,0.5); }
        .vm-btn-outline {
          background: transparent; color: var(--light);
          border: 2px solid var(--gray) !important;
        }
        .vm-btn-outline:hover { border-color: var(--primary) !important; color: var(--accent); transform: translateY(-3px); }

        /* Sections */
        .vm-section { padding: 6rem 5%; position: relative; }
        .vm-section-header { text-align: center; margin-bottom: 4rem; }
        .vm-section-header h2 {
          font-family: 'Orbitron', sans-serif;
          font-size: clamp(1.8rem, 3.5vw, 2.8rem);
          margin-bottom: 1rem; position: relative; display: inline-block;
        }
        .vm-section-header h2::after {
          content: ''; position: absolute; bottom: -10px; left: 50%;
          transform: translateX(-50%); width: 60px; height: 3px;
          background: var(--gradient);
        }
        .vm-section-header p { opacity: 0.7; max-width: 600px; margin: 1.5rem auto 0; line-height: 1.7; }

        /* About */
        .vm-about { background: var(--dark-light); }
        .vm-about-grid {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 4rem; align-items: center; max-width: 1200px; margin: 0 auto;
        }
        .vm-about-image { position: relative; display: flex; align-items: center; justify-content: center; }
        .vm-about-image::before {
          content: ''; position: absolute; top: -20px; left: -20px;
          width: calc(100% + 40px); height: calc(100% + 40px); border: 2px solid var(--primary);
          border-radius: 20px; z-index: 0;
        }
        .vm-about-image img { width: 100%; border-radius: 20px; box-shadow: 0 20px 60px rgba(0,0,0,0.5); position: relative; z-index: 1; }
        .vm-about-content h3 {
          font-family: 'Orbitron', sans-serif; font-size: 1.4rem;
          margin-bottom: 1rem; color: var(--accent);
        }
        .vm-about-content p { margin-bottom: 1rem; line-height: 1.8; opacity: 0.88; }
        .vm-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.5rem; margin-top: 2rem; }
        .vm-stat-item {
          text-align: center; padding: 1.5rem;
          background: var(--gray); border-radius: 15px;
          border: 1px solid rgba(0,112,112,0.15);
        }
        .vm-stat-number { font-family: 'Orbitron', sans-serif; font-size: 1.8rem; color: var(--primary); font-weight: 700; }
        .vm-stat-label { font-size: 0.8rem; opacity: 0.7; margin-top: 0.4rem; }

        /* Services */
        .vm-services-grid {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 2rem; max-width: 1400px; margin: 0 auto;
        }
        .vm-service-card {
          background: var(--dark-light); border-radius: 20px; padding: 2.5rem;
          text-align: center; border: 1px solid rgba(255,255,255,0.05);
          transition: all 0.4s; position: relative; overflow: hidden;
        }
        .vm-service-card::before {
          content: ''; position: absolute; top: 0; left: 0;
          width: 100%; height: 4px; background: var(--gradient);
          transform: scaleX(0); transition: transform 0.4s;
        }
        .vm-service-card:hover::before { transform: scaleX(1); }
        .vm-service-card:hover {
          transform: translateY(-10px);
          border-color: rgba(0,112,112,0.3);
          box-shadow: 0 20px 40px rgba(0,0,0,0.4);
        }
        .vm-service-icon {
          width: 80px; height: 80px; background: var(--gray);
          border-radius: 20px; display: flex; align-items: center;
          justify-content: center; margin: 0 auto 1.5rem;
          font-size: 2rem; color: var(--primary); transition: all 0.4s;
        }
        .vm-service-card:hover .vm-service-icon {
          background: var(--primary); color: var(--white);
          transform: rotateY(360deg);
        }
        .vm-service-card h3 {
          font-family: 'Orbitron', sans-serif; font-size: 1rem;
          margin-bottom: 1rem; line-height: 1.4;
        }
        .vm-service-card p { font-size: 0.88rem; opacity: 0.7; line-height: 1.6; }

        /* Gallery */
        .vm-gallery { background: var(--dark-light); }
        .vm-gallery-grid {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem; max-width: 1400px; margin: 0 auto;
        }
        .vm-gallery-item {
          position: relative; border-radius: 15px; overflow: hidden;
          aspect-ratio: 4/3; cursor: pointer;
        }
        .vm-gallery-item img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s; }
        .vm-gallery-item:hover img { transform: scale(1.1); }
        .vm-gallery-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.85), transparent);
          display: flex; align-items: flex-end; padding: 1.5rem;
          opacity: 0; transition: opacity 0.3s;
        }
        .vm-gallery-item:hover .vm-gallery-overlay { opacity: 1; }
        .vm-gallery-overlay h4 { font-family: 'Orbitron', sans-serif; color: var(--white); font-size: 0.9rem; }

        /* Why Us */
        .vm-why { background: var(--navy); }
        .vm-why-grid {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 2rem; max-width: 1200px; margin: 0 auto;
        }
        .vm-why-card {
          text-align: center; padding: 2.5rem 2rem;
          border: 1px solid rgba(0,112,112,0.2); border-radius: 20px;
          transition: all 0.4s; background: rgba(0,112,112,0.05);
        }
        .vm-why-card:hover {
          background: rgba(0,112,112,0.15); border-color: var(--primary);
          transform: translateY(-8px); box-shadow: 0 15px 35px rgba(0,0,0,0.3);
        }
        .vm-why-icon {
          width: 70px; height: 70px; background: rgba(0,112,112,0.2);
          border-radius: 50%; display: flex; align-items: center;
          justify-content: center; margin: 0 auto 1.5rem;
          font-size: 1.8rem; color: var(--accent); transition: all 0.3s;
        }
        .vm-why-card:hover .vm-why-icon { background: var(--primary); color: var(--white); }
        .vm-why-card h3 { font-family: 'Orbitron', sans-serif; font-size: 1.1rem; margin-bottom: 0.8rem; color: var(--accent); }
        .vm-why-card p { font-size: 0.88rem; opacity: 0.75; line-height: 1.6; }

        /* Contact */
        .vm-contact { background: var(--dark-light); }
        .vm-contact-grid {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 4rem; max-width: 1200px; margin: 0 auto;
        }
        .vm-contact-info h3 { font-family: 'Orbitron', sans-serif; font-size: 1.3rem; margin-bottom: 2rem; }
        .vm-contact-item { display: flex; align-items: flex-start; gap: 1rem; margin-bottom: 1.5rem; }
        .vm-contact-icon {
          width: 50px; height: 50px; background: var(--gray);
          border-radius: 12px; display: flex; align-items: center;
          justify-content: center; color: var(--primary); font-size: 1.2rem; flex-shrink: 0;
          transition: all 0.3s;
        }
        .vm-contact-item:hover .vm-contact-icon { background: var(--primary); color: var(--white); }
        .vm-contact-item div h4 { font-size: 0.95rem; margin-bottom: 0.3rem; font-weight: 600; }
        .vm-contact-item div p { opacity: 0.7; font-size: 0.88rem; line-height: 1.5; }
        .vm-map { border-radius: 20px; overflow: hidden; height: 280px; margin-top: 2rem; border: 1px solid rgba(255,255,255,0.08); }
        .vm-map iframe { width: 100%; height: 100%; border: none; }

        .vm-contact-form {
          background: var(--dark); padding: 3rem;
          border-radius: 20px; border: 1px solid rgba(255,255,255,0.05);
        }
        .vm-contact-form h3 { font-family: 'Orbitron', sans-serif; font-size: 1.3rem; margin-bottom: 2rem; }
        .vm-form-group { margin-bottom: 1.5rem; }
        .vm-form-group label { display: block; margin-bottom: 0.5rem; font-weight: 500; font-size: 0.88rem; opacity: 0.9; }
        .vm-form-group input,
        .vm-form-group textarea,
        .vm-form-group select {
          width: 100%; padding: 0.9rem 1rem;
          background: var(--gray); border: 1px solid rgba(255,255,255,0.08);
          border-radius: 10px; color: var(--light);
          font-family: 'Inter', sans-serif; font-size: 0.9rem;
          transition: all 0.3s; outline: none;
        }
        .vm-form-group input:focus,
        .vm-form-group textarea:focus,
        .vm-form-group select:focus {
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(0,112,112,0.15);
        }
        .vm-form-group textarea { resize: vertical; min-height: 120px; }
        .vm-form-group select option { background: var(--gray); }
        .vm-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
        .vm-success-msg {
          background: rgba(0,112,112,0.2); border: 1px solid var(--primary);
          color: var(--accent); padding: 1rem; border-radius: 10px;
          text-align: center; font-size: 0.9rem; margin-bottom: 1rem;
        }

        /* Footer */
        .vm-footer { background: var(--dark); border-top: 1px solid rgba(255,255,255,0.05); padding: 4rem 5% 2rem; }
        .vm-footer-grid {
          display: grid; grid-template-columns: 2fr 1fr 1fr 1fr;
          gap: 3rem; max-width: 1200px; margin: 0 auto 3rem;
        }
        .vm-footer-brand p { opacity: 0.65; font-size: 0.88rem; line-height: 1.7; margin: 1rem 0 1.5rem; }
        .vm-social-links { display: flex; gap: 0.8rem; }
        .vm-social-links a {
          width: 40px; height: 40px; background: var(--gray);
          border-radius: 10px; display: flex; align-items: center;
          justify-content: center; color: var(--light); text-decoration: none;
          transition: all 0.3s;
        }
        .vm-social-links a:hover { background: var(--primary); color: var(--white); transform: translateY(-3px); }
        .vm-footer-col h4 { font-family: 'Orbitron', sans-serif; font-size: 0.9rem; margin-bottom: 1.5rem; color: var(--accent); }
        .vm-footer-col ul { list-style: none; padding: 0; }
        .vm-footer-col ul li { margin-bottom: 0.8rem; }
        .vm-footer-col ul li a {
          color: var(--light); text-decoration: none; opacity: 0.65;
          transition: all 0.3s; font-size: 0.88rem;
        }
        .vm-footer-col ul li a:hover { opacity: 1; color: var(--accent); padding-left: 5px; }
        .vm-footer-bottom {
          text-align: center; padding-top: 2rem;
          border-top: 1px solid rgba(255,255,255,0.05);
          opacity: 0.55; font-size: 0.82rem;
        }

        /* Lightbox */
        .vm-lightbox {
          display: none; position: fixed; inset: 0;
          background: rgba(0,0,0,0.95); z-index: 2000;
          align-items: center; justify-content: center; padding: 2rem;
        }
        .vm-lightbox.active { display: flex; }
        .vm-lightbox img { max-width: 90%; max-height: 90%; border-radius: 10px; }
        .vm-lightbox-close {
          position: absolute; top: 2rem; right: 2rem;
          font-size: 2rem; color: var(--white); cursor: pointer;
          background: none; border: none;
        }

        /* Fade-in animation */
        .vm-fade { opacity: 0; transform: translateY(30px); transition: all 0.6s ease-out; }
        .vm-fade.vm-visible { opacity: 1; transform: translateY(0); }

        /* Scrollbar */
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: var(--dark); }
        ::-webkit-scrollbar-thumb { background: var(--primary); border-radius: 4px; }

        /* Responsive */
        @media (max-width: 968px) {
          .vm-nav-links {
            display: none; position: absolute; top: 100%; left: 0;
            width: 100%; background: var(--dark); flex-direction: column;
            padding: 2rem; gap: 1.5rem;
            border-bottom: 1px solid rgba(0,112,112,0.2);
          }
          .vm-nav-links.active { display: flex; }
          .vm-mobile-btn { display: block; }
          .vm-about-grid, .vm-contact-grid { grid-template-columns: 1fr; }
          .vm-footer-grid { grid-template-columns: 1fr 1fr; }
          .vm-stats { grid-template-columns: 1fr; }
          .vm-form-row { grid-template-columns: 1fr; }
        }
        @media (max-width: 600px) {
          .vm-footer-grid { grid-template-columns: 1fr; }
          .vm-hero h1 { font-size: 1.9rem; }
          .vm-contact-form { padding: 2rem 1.5rem; }
        }
      `}</style>

      {/* Particle Canvas */}
      <canvas id="vm-canvas" ref={canvasRef} />

      {/* ── Navigation ── */}
      <nav className={`vm-nav${navScrolled ? ' scrolled' : ''}`}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span className="vm-logo-text">VM NETWORKS</span>
          <span className="vm-logo-sub italic">             Solutions Informatiques</span>
        </div>
        <ul className={`vm-nav-links${menuOpen ? ' active' : ''}`} id="vmNavLinks">
          {navLinks.map((link) =>
          <li key={link.href}>
              <a href={link.href} onClick={() => setMenuOpen(false)}>{link.label}</a>
            </li>
          )}
        </ul>
        <button className="vm-mobile-btn" onClick={() => setMenuOpen((o) => !o)} aria-label="Menu">
          <i className={menuOpen ? 'fas fa-times' : 'fas fa-bars'} />
        </button>
      </nav>

      {/* ── Hero ── */}
      <section className="vm-hero" id="home">
        <div className="vm-hero-content">
          <h1>
            <span>VOTRE MATÉRIEL</span><br />
            <span className="vm-gradient-text">ENTRE DE BONNES MAINS</span>
          </h1>
          <p>Des services rapides, fiables et professionnels pour tous vos besoins informatiques.</p>
          <div className="vm-cta-buttons">
            <a href="#services" className="vm-btn vm-btn-primary">Nos Services</a>
            <a href="#contact" className="vm-btn vm-btn-outline">Contactez-nous</a>
          </div>
        </div>
      </section>

      {/* ── About ── */}
      <section className="vm-section vm-about" id="about">
        <div className="vm-section-header">
          <h2>À Propos</h2>
          <p>Votre partenaire de confiance pour tous vos besoins informatiques à Agadir</p>
        </div>
        <div className="vm-about-grid vm-fade">
          <div className="vm-about-content">
            <h3>VM NETWORKS</h3>
            <p>
              VM Networks est une entreprise marocaine spécialisée dans les solutions informatiques professionnelles.
              Basée à Agadir, nous intervenons rapidement pour répondre à tous vos besoins en matière de matériel et de réseau.
            </p>
            <p>
              Notre équipe de techniciens qualifiés met son expertise à votre service pour garantir la performance
              et la fiabilité de vos équipements informatiques, que vous soyez un particulier ou une entreprise.
            </p>
            <p>
              De la réparation à l&apos;installation réseau, en passant par la vente de matériel, nous couvrons
              l&apos;ensemble de vos besoins informatiques avec professionnalisme et réactivité.
            </p>
            <div className="vm-stats">
              <div className="vm-stat-item">
                <div className="vm-stat-number">5+</div>
                <div className="vm-stat-label">Services proposés</div>
              </div>
              <div className="vm-stat-item">
                <div className="vm-stat-number">100%</div>
                <div className="vm-stat-label">Satisfaction client</div>
              </div>
              <div className="vm-stat-item">
                <div className="vm-stat-number">24h</div>
                <div className="vm-stat-label">Délai d&apos;intervention</div>
              </div>
            </div>
          </div>
          <div className="vm-about-image">
            <img
              src="/assets/images/VM_3d-1786100071286.png"
              alt="Logo VM Networks"
              style={{ maxWidth: '320px', width: '100%', borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.4)', position: 'relative', zIndex: 1 }}
            />
          </div>
        </div>
      </section>

      {/* ── Services ── */}
      <section className="vm-section" id="services" style={{ background: 'var(--dark)' }}>
        <div className="vm-section-header">
          <h2>NOS SERVICES</h2>
          <p>Des services rapides, fiables et professionnels pour tous vos besoins informatiques</p>
        </div>
        <div className="vm-services-grid">
          {services.map((s, i) =>
          <div key={i} className="vm-service-card vm-fade">
              <div className="vm-service-icon">
                <i className={s.icon} />
              </div>
              <h3>{s.title}</h3>
              <p>{s.description}</p>
            </div>
          )}
        </div>
      </section>

      {/* ── Gallery ── */}
      <section className="vm-section vm-gallery" id="gallery">
        <div className="vm-section-header">
          <h2>Galerie</h2>
          <p>Découvrez nos réalisations et interventions</p>
        </div>
        <div className="vm-gallery-grid">
          {galleryItems.map((item, i) =>
          <div
            key={i}
            className="vm-gallery-item vm-fade"
            onClick={() => {setLightboxSrc(item.src);setLightboxAlt(item.alt);}}>
            
              <Image
              src={item.src}
              alt={item.alt}
              width={600}
              height={400}
              style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s' }} />
            
              <div className="vm-gallery-overlay">
                <h4>{item.label}</h4>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── Why Us ── */}
      <section className="vm-section vm-why" id="why">
        <div className="vm-section-header">
          <h2>POURQUOI NOUS CHOISIR ?</h2>
          <p>Ce qui nous distingue et fait notre réputation à Agadir</p>
        </div>
        <div className="vm-why-grid">
          {whyItems.map((item, i) =>
          <div key={i} className="vm-why-card vm-fade">
              <div className="vm-why-icon">
                <i className={item.icon} />
              </div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </div>
          )}
        </div>
      </section>

      {/* ── Contact ── */}
      <section className="vm-section vm-contact" id="contact">
        <div className="vm-section-header">
          <h2>CONTACTEZ-NOUS</h2>
          <p>Discutons de vos besoins informatiques</p>
        </div>
        <div className="vm-contact-grid">
          {/* Info */}
          <div className="vm-contact-info">
            <h3>Informations de Contact</h3>
            <div className="vm-contact-item">
              <div className="vm-contact-icon"><i className="fas fa-map-marker-alt" /></div>
              <div>
                <h4>NOTRE ADRESSE</h4>
                <p>N° 842 Etage 4 Imm 8 Residence Khalij Annakhil Founty, AGADIR</p>
              </div>
            </div>
            <div className="vm-contact-item">
              <div className="vm-contact-icon"><i className="fas fa-mobile-alt" /></div>
              <div>
                <h4>Mobile</h4>
                <p>+212 6 61 28 60 75</p>
              </div>
            </div>
            <div className="vm-contact-item">
              <div className="vm-contact-icon"><i className="fas fa-phone" /></div>
              <div>
                <h4>Téléphone Fixe</h4>
                <p>+212 5 28 20 23 36</p>
              </div>
            </div>
            <div className="vm-contact-item">
              <div className="vm-contact-icon"><i className="fas fa-envelope" /></div>
              <div>
                <h4>Email</h4>
                <p>tayeb@vmnetworks.ma</p>
                <p>vmnetworks.maroc@gmail.com</p>
              </div>
            </div>
            <div className="vm-contact-item">
              <div className="vm-contact-icon"><i className="fas fa-globe" /></div>
              <div>
                <h4>Website</h4>
                <p>www.vmnetworks.ma</p>
              </div>
            </div>
            <div className="vm-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3441.2973052586512!2d-9.58929452443257!3d30.39930567474687!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xdb3b7001f48b7ad%3A0x1993b7f880054512!2sVM%20NETWORKS!5e0!3m2!1sfr!2sma!4v1785442383612!5m2!1sfr!2sma"
                allowFullScreen
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                title="VM Networks Agadir" />
              
            </div>
          </div>

          {/* Form */}
          <div className="vm-contact-form">
            <h3>Envoyez-nous un message</h3>
            {formSent &&
            <div className="vm-success-msg">
                <i className="fas fa-check-circle" style={{ marginRight: '0.5rem' }} />
                Message envoyé avec succès ! Nous vous répondrons rapidement.
              </div>
            }
            <form onSubmit={handleFormSubmit}>
              <div className="vm-form-row">
                <div className="vm-form-group">
                  <label htmlFor="nom">Nom</label>
                  <input id="nom" name="nom" type="text" value={formState.nom} onChange={handleFormChange} required />
                </div>
                <div className="vm-form-group">
                  <label htmlFor="prenom">Prénom</label>
                  <input id="prenom" name="prenom" type="text" value={formState.prenom} onChange={handleFormChange} required />
                </div>
              </div>
              <div className="vm-form-group">
                <label htmlFor="email">Email</label>
                <input id="email" name="email" type="email" value={formState.email} onChange={handleFormChange} required />
              </div>
              <div className="vm-form-group">
                <label htmlFor="telephone">Téléphone</label>
                <input id="telephone" name="telephone" type="tel" value={formState.telephone} onChange={handleFormChange} />
              </div>
              <div className="vm-form-group">
                <label htmlFor="service">Service concerné</label>
                <select id="service" name="service" value={formState.service} onChange={handleFormChange}>
                  <option value="">Sélectionnez un service</option>
                  <option>Réparation PC &amp; Imprimantes</option>
                  <option>Vente de Matériels Informatiques</option>
                  <option>Maintenance &amp; Diagnostic</option>
                  <option>Installation &amp; Entretien Réseau</option>
                  <option>Assistance &amp; Conseils</option>
                  <option>Détection Incendie</option>
                  <option>Vidéo Surveillance</option>
                  <option>Téléphonie</option>
                </select>
              </div>
              <div className="vm-form-group">
                <label htmlFor="message">Message</label>
                <textarea id="message" name="message" value={formState.message} onChange={handleFormChange} required />
              </div>
              {formError &&
              <div style={{ background: '#3a1a1a', color: '#ff6b6b', border: '1px solid #ff4444', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.9rem' }}>
                  <i className="fas fa-exclamation-circle" style={{ marginRight: '0.5rem' }} />
                  {formError}
                </div>
              }
              <button type="submit" className="vm-btn vm-btn-primary" style={{ width: '100%' }} disabled={formLoading}>
                {formLoading ? 'Envoi en cours...' : 'Envoyer le message'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="vm-footer">
        <div className="vm-footer-grid">
          <div className="vm-footer-brand">
            <div style={{ marginBottom: '0.5rem' }}>
              <span className="vm-logo-text" style={{ fontSize: '1.4rem' }}>VM NETWORKS</span>
              <span className="vm-logo-sub" style={{ marginTop: '2px' }}>Solutions Informatiques</span>
            </div>
            <p>
              Votre matériel entre de bonnes mains. Votre partenaire de confiance
              pour tous vos besoins informatiques à Agadir.
            </p>
            
          </div>
          <div className="vm-footer-col">
            <h4>Services</h4>
            <ul>
              <li><a href="#services">Réparation PC</a></li>
              <li><a href="#services">Vente Matériel</a></li>
              <li><a href="#services">Maintenance</a></li>
              <li><a href="#services">Réseau</a></li>
              <li><a href="#services">Assistance</a></li>
              <li><a href="#services">Détection Incendie</a></li>
              <li><a href="#services">Vidéo Surveillance</a></li>
              <li><a href="#services">Téléphonie</a></li>
            </ul>
          </div>
          <div className="vm-footer-col">
            <h4>Entreprise</h4>
            <ul>
              <li><a href="#about">À Propos</a></li>
              <li><a href="#why">Pourquoi Nous</a></li>
              <li><a href="#gallery">Galerie</a></li>
              <li><a href="#contact">Contact</a></li>
            </ul>
          </div>
          <div className="vm-footer-col">
            <h4>Contact</h4>
            <ul>
              <li><a href="tel:+212661286075">+212 6 61 28 60 75</a></li>
              <li><a href="tel:+212528202336">+212 5 28 20 23 36</a></li>
              <li><a href="http://www.vmnetworks.ma">www.vmnetworks.ma</a></li>
              <li><a href="#contact">Agadir, Maroc</a></li>
            </ul>
          </div>
        </div>
        <div className="vm-footer-bottom">
          <p>© 2025 VM Networks Solutions Informatiques. Tous droits réservés. | www.vmnetworks.ma</p>
        </div>
      </footer>

      {/* ── Lightbox ── */}
      <div
        className={`vm-lightbox${lightboxSrc ? ' active' : ''}`}
        onClick={() => setLightboxSrc(null)}>
        
        <button className="vm-lightbox-close" onClick={() => setLightboxSrc(null)} aria-label="Fermer">
          <i className="fas fa-times" />
        </button>
        {lightboxSrc &&
        <Image
          src={lightboxSrc}
          alt={lightboxAlt}
          width={900}
          height={600}
          style={{ maxWidth: '90%', maxHeight: '90%', borderRadius: '10px' }} />

        }
      </div>
    </>);

}